import { ConversationPreview } from '../../../../../store/chat/types';

type GroupedConversations = {
  [key: string]: ConversationPreview[];
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date: Date): boolean => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date: Date): boolean => {
  const pastWeek = new Date();
  pastWeek.setDate(pastWeek.getDate() - 7);
  return date >= pastWeek && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date: Date): boolean => {
  const pastMonth = new Date();
  pastMonth.setMonth(pastMonth.getMonth() - 1);
  return date >= pastMonth && !isPastWeek(date) && !isToday(date) && !isYesterday(date);
};

const getMonthYearKey = (date: Date): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const month = months[date.getMonth()];
  const year = date.getFullYear();

  const currentYear = new Date().getFullYear();

  return currentYear === year ? month : `${month} ${year}`;
};

export const groupConversationsByDate = (
  conversations: ConversationPreview[],
): GroupedConversations => {
  const groups: GroupedConversations = {
    Today: [],
    Yesterday: [],
    'Past Week': [],
    'Past Month': [],
  };

  // Create a copy of the conversations array to avoid mutating the original
  const sortedConversations = [...conversations].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  sortedConversations.forEach((conversation) => {
    const date = new Date(conversation.created_at);

    // Add validation to handle potential invalid dates
    if (isNaN(date.getTime())) {
      console.warn('Invalid date detected in conversation, skipping this conversation');
      return; // Skip this conversation
    }

    if (isToday(date)) {
      groups['Today'].push(conversation);
    } else if (isYesterday(date)) {
      groups['Yesterday'].push(conversation);
    } else if (isPastWeek(date)) {
      groups['Past Week'].push(conversation);
    } else if (isPastMonth(date)) {
      groups['Past Month'].push(conversation);
    } else {
      const key = getMonthYearKey(date);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(conversation);
    }
  });

  // Filter out empty groups
  return Object.entries(groups).reduce((acc, [key, value]) => {
    if (value.length > 0) {
      acc[key] = value;
    }
    return acc;
  }, {} as GroupedConversations);
};

export const getGroupKeys = (groupedConversations: GroupedConversations): string[] => {
  const fixedOrder = ['Today', 'Yesterday', 'Past Week', 'Past Month'];

  // Get all keys from grouped conversations
  const keys = Object.keys(groupedConversations);

  // Filter fixed order keys that exist in the grouped conversations
  const orderedKeys = fixedOrder.filter((key) => keys.includes(key));

  // Get remaining keys (months)
  const monthKeys = keys.filter((key) => !fixedOrder.includes(key));

  // Sort month keys
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  monthKeys.sort((a, b) => {
    // Check if the keys include years
    const aHasYear = a.includes(' ');
    const bHasYear = b.includes(' ');

    if (aHasYear && bHasYear) {
      // Both have years, compare years first, then months
      const [aMonth, aYear] = a.split(' ');
      const [bMonth, bYear] = b.split(' ');

      if (aYear !== bYear) {
        return Number(bYear) - Number(aYear); // More recent year first
      }

      return months.indexOf(aMonth) - months.indexOf(bMonth);
    } else if (aHasYear) {
      return 1; // a has year, should come after b
    } else if (bHasYear) {
      return -1; // b has year, should come after a
    } else {
      // Neither has year, just compare months
      return months.indexOf(a) - months.indexOf(b);
    }
  });

  // Return ordered keys followed by month keys
  return [...orderedKeys, ...monthKeys];
};
